<template>
  <div class="search-bar" :class="rootClasses" :key="itemPosition">
    <div class="search-bar__container">
      <input
        class="search-bar__input"
        type="text"
        v-model="input"
        @click="openTypeahead"
        @focusin="openTypeahead"
        @focusout="handleFocusOut"
        @input="debounceLookup"
        @keydown.down.prevent="handleDown"
        @keydown.up.prevent="handleUp"
        @keydown.enter="handleEnter"
        @keydown.esc="closeTypeahead"
        :placeholder="placeholder" />
      <button class="search-bar__action-container" @click="handleMagnifyingGlassClick">
        <icon-magnifying-lens class="search-bar__icon icon-magnifying-lens" />
      </button>
    </div>
    <typeahead-results
      v-if="typeaheadOpen && input"
      :typeahead-loading="typeaheadLoading"
      :typeahead-error="typeaheadError"
      :typeahead-focus="typeaheadFocus"
      :typeahead-results="typeaheadResults"
      :search-term="input"
      @focusElement="setFocusElement($event)"/>
  </div>
</template>

<script lang="ts">
import { startListening, stopListening } from "~/common/ts/clickAway";
import { debounce } from "~/common/ts/debounce";
import { defaultShopRoute } from "~/common/ts/storeHelpers";
import { useSearchStore } from "~/stores/searchStore";
import { defineAsyncComponent, useRuntimeConfig } from "#imports";

const TypeaheadResults = defineAsyncComponent(() => import("~/components/parts/search/TypeaheadResults.vue"));
const IconMagnifyingLens = defineAsyncComponent(() => import('~/assets/icons/IconMagnifyingLens.svg'));

export default {
  setup () {
    return {
      searchStore: useSearchStore(),
      config: useRuntimeConfig(),
    };
  },
  components: {
    TypeaheadResults,
    IconMagnifyingLens,
  },
  props: {
    barSize: {
      type: String,
      default: 'medium',
    },
    itemPosition: {
      type: String,
      default: 'content',
    },
  },
  emits: [
    'on-search',
  ],
  data () {
    return {
      input: '',
      mobileSearchOpen: false,
      typeaheadOpen: false,
      typeaheadLoading: false,
      typeaheadResults: {
        products: [],
        vendors: [],
        categories: [],
        blogPosts: [],
      },
      typeaheadError: null,
      typeaheadFocus: null,
      typeaheadFocusElement: null,
      typeaheadHistory: {},
    };
  },
  watch: {
    $route () {
      this.closeTypeahead();
    },
  },
  computed: {
    rootClasses () {
      return {
        'search-bar--large': this.barSize === 'large',
        'search-bar--medium': this.barSize === 'medium',
        'search-bar--compact': this.barSize === 'compact',
        'search-bar--in-header': this.itemPosition === 'header',
        'search-bar--blog-only': this.itemPosition === 'blog',
        'search-bar--typeahead-open': this.typeaheadOpen,
        'search-bar--on-search-page': this.itemPosition === 'search',
        'search-bar--mobile-open': this.mobileSearchOpen,
      }
    },
    placeholder () {
      if (this.barSize === 'compact') {
        return 'Search'
      }

      return 'Enter your search term';
    },
    inputCacheKey () {
      return this.input;
    }
  },
  methods: {
    setFocusElement (element) {
      this.typeaheadFocusElement = element;
    },
    debounceLookup () {
      // Make sure that the typeahead is visible if something is inputted.
      // This can happen if someone hits enter and then starts typing again.
      this.typeaheadOpen = true;

      if (this.input && this.typeaheadHistory[this.inputCacheKey]) {
        this.typeaheadResults = this.typeaheadHistory[this.inputCacheKey];
      } else {
        this.typeaheadLoading = true;
        debounce(this.lookUpResults);
      }
    },
    lookUpResults () {
      this.typeaheadFocus = null;

      if (this.input.length >= 2) {
        if (this.typeaheadHistory[this.inputCacheKey]) {
          this.typeaheadResults = this.typeaheadHistory[this.inputCacheKey];
          this.typeaheadLoading = false;
        } else {
          this.typeaheadLoading = true;
          const inputCopy = this.input.toString();
          const cacheKeyCopy = this.inputCacheKey.toString();
          const isOnBlogPage = this.itemPosition === 'blog';
          let searchParam = isOnBlogPage
            ? 'blog'
            : 'general-typeahead';

          if ('search' === this.itemPosition) {
            searchParam = 'product-typeahead'
          }

          fetch(`${this.config.public.baseUrl}/wp-json/content-search/v1/search?type=${searchParam}&query=${inputCopy}`)
            .then(async (response) => {
              const data = await response.json();
              // store a history of all search terms to display results quickly on term change
              this.typeaheadHistory[cacheKeyCopy] = data;

              // Do not display the current result if the search term has changed in the meantime
              if (this.input === inputCopy) {
                if (isOnBlogPage) {
                  this.typeaheadResults.blogPosts = data.results;
                } else {
                  this.typeaheadResults = data;
                }

                this.typeaheadLoading = false;
              }
            })
            .catch((e) => {
              this.typeaheadError = e.toString();
              this.typeaheadResults = [];
              this.typeaheadLoading = false;
            });
        }
      }
    },
    openTypeahead () {
      this.typeaheadOpen = true;
      startListening(this.$el, this.closeTypeahead);
    },
    closeTypeahead () {
      this.typeaheadOpen = false;
      this.typeaheadError = null;
      this.typeaheadFocus = null;
      this.typeaheadFocusElement = null;
      stopListening(this.$el, this.closeTypeahead);
    },
    handleDown () {
      if (this.typeaheadFocus === null) {
        this.typeaheadFocus = 0;
      } else {
        this.typeaheadFocus += 1;
      }
    },
    handleUp () {
      if (this.typeaheadFocus === null) {
        this.typeaheadFocus = 0;
      } else {
        this.typeaheadFocus -= 1;
      }
    },
    handleEnter () {
      if (this.typeaheadFocusElement !== null) {
        const elem = this.typeaheadFocusElement;

        if (elem.link) {
          this.closeTypeahead();
          this.input = '';
          window.location.assign(elem.link);
          return;
        }
      }

      // emit event instead of redirect if used as searchbar on search or blog list page
      // but only if you are on the sound effect page and want to search for sound effects, else redirect
      if (['search', 'blog'].includes(this.itemPosition)) {
        this.$emit('on-search', {
          input: this.input,
        });
      } else {
        this.$router.push(`${defaultShopRoute}?query=${this.input}`);
      }

      this.input = '';
      this.closeTypeahead();
    },
    handleFocusOut () {
      this.typeaheadFocus = null;
    },
    handleMagnifyingGlassClick () {
      if (this.itemPosition === 'header' && window.innerWidth < 1024) {
        this.$router.push('/shop/');
      } else {
        this.handleEnter();
      }
    }
  },
};
</script>

<style lang="scss">
@use "@vueform/multiselect/themes/default";
@use "~/common/scss/variables";
@use "~/common/scss/helpers";
@use "~/common/scss/button";
@use "~/common/scss/mq";

.icon-magnifying-lens {
  margin: .6rem 0 0 .7rem;
  font-size: 1rem;
}

.home-stage__search-bar .search-bar__icon {
  width: 60%;
  height: 60%;
  margin: 0;
  padding: 0;
}

.search-bar {
  $root: &;

  --border-color: transparent;
  --bar-height: #{helpers.px-to-rem(59)};

  position: relative;
  max-width: helpers.px-to-rem(790);
  width: 100%;

  &--compact {
    --bar-height: #{helpers.px-to-rem(40)};
    --border-color: #{variables.$colorBlack};

    max-width: helpers.px-to-rem(250);

    &#{$root}--in-header {
      @include mq.mq(variables.$mq-2-max) {
        width: auto;

        #{$root}__container {
          border: none;
        }

        #{$root}__input {
          display: none;
        }

        #{$root}__action-container {
          border-radius: 5px;
        }
      }
    }
  }

  &--large {
    max-width: variables.$containerLarge;
    margin: 0 auto;
  }

  &--blog-only {
    --bar-height: #{helpers.px-to-rem(42)};

    max-width: 100%;
  }

  &__container {
    align-items: stretch;
    background-color: #FFF;
    border: 1px solid var(--border-color);
    border-radius: 3px;
    color: variables.$colorBlack;
    display: grid;
    font-size: helpers.px-to-rem(12);
    grid-template: "input action" auto / 1fr auto;
    height: var(--bar-height);
    margin: 0 auto;
    width: 100%;

    #{$root}--medium & {
      font-size: helpers.px-to-rem(16);
      padding: .25rem .25rem;
    }

    #{$root}--large & {
      font-size: helpers.px-to-rem(16);
      padding: .25rem .25rem;
      border: 1px solid #707070;
    }

    #{$root}--blog-only & {
      background-color: #272727;
      color: #FFFFFF;
      max-width: 100%;

      input::placeholder {
        color: #B4B4B4;
      }
    }

    #{$root}--typeahead-open & {
      border-radius: 3px 3px 0 0;
    }
  }

  &__select-for-container {
    --ms-option-bg-selected: #{variables.$colorOrange};
    --ms-option-bg-selected-pointed: #{variables.$colorOrange};
    --ms-bg: transparent;
    --ms-radius: 0;

    align-items: center;
    color: #000;
    display: flex;
    grid-area: dropdown;
    height: 100%;
    justify-content: center;

    #{$root}--compact & {
      width: helpers.px-to-rem(50);
    }

    .multiselect {
      border: none;
      height: 100%;
    }

    .multiselect-wrapper {
      gap: .25rem;
      padding: .25rem .25rem;

      #{$root}--medium &,
      #{$root}--large & {
        height: 100%;
        padding: .25rem helpers.px-to-rem(20);
      }
    }

    .multiselect-dropdown {
      min-width: 100%;
      right: unset;
      width: max-content;
    }

    .multiselect-option {
      gap: .5rem;
    }
  }

  &__input {
    color: currentColor;
    height: 100%;
    padding: .25rem 1rem;
    width: 100%;

    &:focus {
      outline: none;
    }
  }

  &__action-container {
    background: transparent;
    border: none;
    min-width: 2.5rem;
    height: 100%;

    #{$root}--medium &,
    #{$root}--large & {
      @include button.buttonBlack();
      @include button.button();
      font-size: helpers.px-to-rem(25);
      width: helpers.px-to-rem(70);
    }
  }

  &__icon {

    &--small {
      width: helpers.px-to-rem(16);
    }
  }
}
</style>
